import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { SlidersHorizontal, X } from 'lucide-react'
import { useMe } from '@/lib/auth'
import { errorMessage, useTRPC } from '@/lib/trpc'
import { Segmented } from './segmented'
import { Toggle } from './toggle'
import { NumericInput } from './numeric-input'

/** 3% is the account default; "Auto" shows for it */
export const AUTO_SLIPPAGE_PERCENT = 3
const PRESETS = [1, 5] as const
type FeeLevel = 'medium' | 'high' | 'veryHigh'
const FEE_OPTIONS: { value: FeeLevel; label: string }[] = [
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'veryHigh', label: 'Very high' },
]

/** The user's saved slippage, as basis points for the swap calls. */
export function useSlippageBps() {
  const { user } = useMe()
  const percent = user?.presets.slippagePercent ?? AUTO_SLIPPAGE_PERCENT
  return Math.max(10, Math.min(5000, Math.round(percent * 100)))
}

/**
 * Slippage and priority fee, Sonar style: a small panel off the shortcuts
 * row. Saved to the account, so swaps, buys, sells and strategies use it
 * everywhere, including the bot.
 */
export function TradeSettings() {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { user, refetch } = useMe()
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (open) void refetch()
  }, [open, refetch])
  const [custom, setCustom] = useState('')
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const slippage = user?.presets.slippagePercent ?? AUTO_SLIPPAGE_PERCENT
  const level = (user?.presets.solanaPriorityFeeLevel ?? 'medium') as string
  const removeInsiderMark = user?.presets.removeInsiderMark ?? true
  const update = useMutation(
    trpc.auth.updateTradeSettings.mutationOptions({
      // the chips move at once; the account catches up behind, and a save that fails puts them back (John, Nov 2 2026)
      onMutate: async (input) => {
        const key = trpc.auth.me.queryKey()
        await queryClient.cancelQueries({ queryKey: key })
        const previous = queryClient.getQueryData(key)
        queryClient.setQueryData(key, (old) =>
          old
            ? {
                ...old,
                presets: {
                  ...old.presets,
                  ...(input.slippagePercent !== undefined ? { slippagePercent: input.slippagePercent } : {}),
                  ...(input.solanaPriorityFeeLevel !== undefined ? { solanaPriorityFeeLevel: input.solanaPriorityFeeLevel } : {}),
                  ...(input.removeInsiderMark !== undefined ? { removeInsiderMark: input.removeInsiderMark } : {}),
                },
              }
            : old,
        )
        return { previous }
      },
      onError: (err, _input, context) => {
        if (context?.previous) queryClient.setQueryData(trpc.auth.me.queryKey(), context.previous)
        setError(errorMessage(err))
      },
      onSettled: () => void queryClient.invalidateQueries({ queryKey: trpc.auth.me.queryKey() }),
    }),
  )
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])
  const isPreset = slippage === AUTO_SLIPPAGE_PERCENT || PRESETS.some((p) => p === slippage)
  const customValue = custom !== '' ? custom : isPreset ? '' : String(slippage)
  function setSlippage(percent: number) {
    setError(null)
    setCustom('')
    update.mutate({ slippagePercent: percent })
  }
  function commitCustom() {
    const n = Number(custom)
    if (!custom.trim()) return
    if (!Number.isFinite(n) || n < 0.01 || n > 100) return setError('Slippage is between 0.01% and 100%.')
    setError(null)
    update.mutate({ slippagePercent: Math.round(n * 100) / 100 })
  }
  return (
    <div className="relative" ref={ref}>
      <button type="button" className="btn btn-ghost !p-1 text-muted hover:text-text" aria-label="Slippage and fees" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <SlidersHorizontal className="size-4" />
      </button>
      {open ? (
        <div className="absolute right-0 top-8 z-30 w-[340px] max-w-[calc(100vw-2rem)] rounded-card border border-line bg-surface p-4 text-sm text-text shadow-2xl">
          <div className="mb-3 flex items-center justify-between">
            <span className="font-medium text-muted">Slippage</span>
            <button type="button" className="btn btn-ghost !p-1" aria-label="Close" onClick={() => setOpen(false)}><X className="size-4" /></button>
          </div>
          <div className="grid grid-cols-5 gap-2 text-sm">
            <button type="button" className="chip !rounded-control justify-center py-2" aria-pressed={slippage === AUTO_SLIPPAGE_PERCENT} onClick={() => setSlippage(AUTO_SLIPPAGE_PERCENT)}>Auto</button>
            {PRESETS.map((p) => (
              <button key={p} type="button" className="chip !rounded-control justify-center py-2" aria-pressed={slippage === p} onClick={() => setSlippage(p)}>{p}%</button>
            ))}
            <label className={`control col-span-2 flex items-center px-2 ${!isPreset ? '!border-accent' : ''}`}>
              <NumericInput
                className="w-full min-w-0 bg-transparent py-1.5 text-sm outline-none placeholder:text-muted"
                placeholder="Custom"
                value={customValue}
                onChange={setCustom}
                onBlur={commitCustom}
                onKeyDown={(e) => { if (e.key === 'Enter') commitCustom() }}
              />
              <span className="text-xs text-muted">%</span>
            </label>
          </div>
          <div className="mt-4 mb-2 font-medium text-muted">Priority fee</div>
          <Segmented
            value={FEE_OPTIONS.some((o) => o.value === level) ? (level as FeeLevel) : 'medium'}
            onChange={(value) => { setError(null); update.mutate({ solanaPriorityFeeLevel: value }) }}
            options={FEE_OPTIONS}
          />
          <div className="mt-4 flex items-center justify-between">
            <span className="font-medium text-muted">Remove Insider Mark</span>
            <Toggle checked={removeInsiderMark} onChange={(checked) => { setError(null); update.mutate({ removeInsiderMark: checked }) }} label="Remove Insider Mark" />
          </div>
          {error ? <p className="mt-2 text-xs text-negative">{error}</p> : null}
        </div>
      ) : null}
    </div>
  )
}
